/**
 * utils/auth.js
 *
 * Funciones auxiliares para gestionar el JWT en localStorage.
 * Centralizar el acceso evita "string literals" repartidos por la app
 * y facilita cambios futuros (p.ej. pasar a cookies secure/HttpOnly).
 */

const KEY = 'token'; // si cambias aquí, cambia en Login, axiosClient, etc.
const REFRESH_KEY = 'refresh_token';

/* ---------- getters ---------- */
export const getAuthToken = () => localStorage.getItem(KEY);
export const getRefreshToken = () => localStorage.getItem(REFRESH_KEY);

/* ---------- setters ---------- */
export const setAuthToken = (token) => {
  if (token) localStorage.setItem(KEY, token);
};
export const setRefreshToken = (token) => {
  if (token) localStorage.setItem(REFRESH_KEY, token);
};

/* ---------- remove ---------- */
export const clearAuthToken = () => localStorage.removeItem(KEY);
export const clearRefreshToken = () => localStorage.removeItem(REFRESH_KEY);

/* ---------- refresh single-flight (cross-tab) ---------- */
const LOCK_KEY = 'refresh_lock';
// LOCK_TTL_MS DEBE ser > REFRESH_FETCH_TIMEOUT_MS: un fetch vivo nunca pierde su lock,
// y una pestaña que crasheó se recupera tras este TTL.
const LOCK_TTL_MS = 20000;
const REFRESH_FETCH_TIMEOUT_MS = 15000;
const WAIT_TIMEOUT_MS = 22000;
const WAIT_POLL_MS = 60;

// Stamp único por pestaña: localStorage no tiene CAS, así que verificamos propiedad.
const TAB_STAMP = `${Date.now().toString(36)}.${Math.random().toString(36).slice(2)}`;
let _myLock = null;

let _inFlight = null;

export function __resetRefreshLock() { _inFlight = null; }
export function __resetCrossTabLock() {
  try { localStorage.removeItem(LOCK_KEY); } catch (e) { /* noop */ }
}

function _lockTs() {
  const v = localStorage.getItem(LOCK_KEY);
  // El valor es "<ts>:<stamp>"; parseInt para en ':', así que el prefijo entero sigue siendo el ts.
  const n = v ? parseInt(v, 10) : 0;
  return Number.isFinite(n) ? n : 0;
}
function _tryAcquireLock() {
  const ts = _lockTs();
  if (ts && Date.now() - ts < LOCK_TTL_MS) return false;
  // Borrow-and-verify: escribimos nuestro stamp y comprobamos que ganamos el write.
  const stamp = `${Date.now()}:${TAB_STAMP}`;
  localStorage.setItem(LOCK_KEY, stamp);
  if (localStorage.getItem(LOCK_KEY) !== stamp) return false; // otra pestaña ganó la carrera
  _myLock = stamp;
  return true;
}
function _releaseLock() {
  try {
    // Solo soltamos si seguimos siendo el dueño (no pisar a un peer que tomó el relevo).
    if (localStorage.getItem(LOCK_KEY) === _myLock) localStorage.removeItem(LOCK_KEY);
  } catch (e) { /* noop */ }
  _myLock = null;
}
const _sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function _waitForPeerToken(prev) {
  const deadline = Date.now() + WAIT_TIMEOUT_MS;
  while (Date.now() < deadline) {
    const t = getAuthToken();
    if (t && t !== prev) return t;
    if (!_lockTs()) break;
    await _sleep(WAIT_POLL_MS);
  }
  const t = getAuthToken();
  return t && t !== prev ? t : null;
}

async function _doRefresh({ backendUrl, failedToken }) {
  const failed = failedToken != null ? failedToken : getAuthToken();

  const current = getAuthToken();
  if (current && current !== failed) return current;

  if (!_tryAcquireLock()) {
    const peerToken = await _waitForPeerToken(failed);
    if (peerToken) return peerToken;
    _tryAcquireLock();
  }

  try {
    const cur2 = getAuthToken();
    if (cur2 && cur2 !== failed) return cur2;

    const refresh = getRefreshToken();
    if (!refresh) throw new Error('no_refresh_token');

    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), REFRESH_FETCH_TIMEOUT_MS);
    let res;
    try {
      res = await fetch(`${String(backendUrl).replace(/\/$/, '')}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refresh }),
        signal: ctrl.signal,
      });
    } catch (e) {
      // Error de red / abort por timeout: NO limpiamos tokens (es transitorio). Re-lanzamos.
      throw e;
    } finally {
      clearTimeout(timer);
    }
    if (!res.ok) {
      clearAuthToken();
      clearRefreshToken();
      throw new Error('refresh_failed_' + res.status);
    }
    const data = await res.json();
    if (data.token) setAuthToken(data.token);
    // COMPARE-AND-SET: solo publicamos el refresh nuevo si en localStorage SIGUE
    // estando el que enviamos. Si otra pestaña ya publicó una generación más nueva,
    // escribir la nuestra sería una REGRESIÓN: dejaríamos guardado un secreto viejo
    // que, al caducar su ventana de gracia, el backend clasificaría como reuso y
    // borraría la familia entera (sesión muerta, "Authorization header missing").
    if (data.refresh_token && data.refresh_token !== refresh) {
      if (getRefreshToken() === refresh) setRefreshToken(data.refresh_token);
    }
    return data.token;
  } finally {
    _releaseLock();
  }
}

export function refreshAccessToken(opts) {
  if (_inFlight) return _inFlight;
  _inFlight = _doRefresh(opts).finally(() => { _inFlight = null; });
  return _inFlight;
}
